import { GetCurrentUserResponse, PendoContextResponse } from "./types";

/**
 * Injects the Pendo tracking script into the webpage.
 * @param pendoConfig - Configuration details fetched from the API.
 */
export async function injectPendoScript(pendoConfig: GetCurrentUserResponse) {
	try {
		const head = document.querySelector('head');
		const script = document.createElement('script');
		script.text = `!function(e,n,t,c,i){var a,o,s,d,p;for((i=e[c]=e[c]||{})._q=i._q||[],o=0,s=(a=["initialize","identify","updateOptions","pageLoad","track"]).length;o<s;++o)!function(e){i[e]=i[e]||function(){i._q[e===a[0]?"unshift":"push"]([e].concat([].slice.call(arguments,0)))}}(a[o]);(d=n.createElement(t)).async=!0,d.src="https://content.pendo.xtm-intl.com/agent/static/${pendoConfig.productExperience.subscriptionId}/pendo.js",(p=n.getElementsByTagName(t)[0]).parentNode.insertBefore(d,p)}(window,document,"script","pendo");`;
		if (head) {
			head.appendChild(script);
		}

		pendo.initialize({
			visitor: {
				id: `${pendoConfig.clientId}-${pendoConfig.userId}`,
				companyName: pendoConfig.clientId,
				userId: pendoConfig.userId,
			},
			account: {
				id: pendoConfig.productExperience.accountId,
			},
		});
	} catch (error) {
		console.warn('Failed to inject Pendo script', error);
	}
}

/**
 * Converts PendoContextResponse to GetCurrentUserResponse format
 * @param pendoContext - The context data from the Pendo endpoint
 * @returns A GetCurrentUserResponse object that can be used with injectPendoScript
 */
export function convertPendoContextToUserData(pendoContext: PendoContextResponse): GetCurrentUserResponse {
	return {
		clientId: pendoContext.client,
		userId: pendoContext.user_id,
		language: 'en',
		userRoles: [],
		zoneId: '',
		productExperience: {
			subscriptionId: 'default',
			accountId: pendoContext.account_id,
			trainingId: '',
		},
	};
}

/**
 * Fetches Pendo context data from the API and initializes Pendo
 * @param sessionId - The session ID to use for the API call
 * @param apiUrl - The base API URL
 */
export const fetchPendoContextAndInitialize = async (sessionId: string, apiUrl: string = process.env.REACT_APP_API_URL || '') => {
	try {
		const response = await fetch(`${apiUrl}/chat/${sessionId}/context/pendo`);

		if (!response.ok) {
			throw new Error('Failed to fetch Pendo context');
		}

		const pendoContext: PendoContextResponse = await response.json();
		const userData = convertPendoContextToUserData(pendoContext);

		await injectPendoScript(userData);
		return true;
	} catch (error) {
		console.warn('Failed to initialize Pendo from context', error);
		return false;
	}
};

/**
 * Initializes Pendo with pre-fetched user data.
 * @param userData - The user data fetched from the API.
 */
export const initializePendo = async (userData: GetCurrentUserResponse) => {
	try {
		await injectPendoScript(userData);
	} catch (error) {
		console.warn('Failed to initialize Pendo', error);
	}
};
